// src/modules/estimates/SendEstimateModal.js
//
// Confirm-and-send dialog for a quote, shared by every "Send" entry point
// (lead detail, estimate detail).
//
// A lead can have several email addresses. When it has exactly one — which is
// almost every lead — this renders the original one-click confirm dialog and
// reports back a null recipient, so the request sent is byte-identical to the
// pre-picker one. Only when there is a real choice does it show a picker.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Modal from '../../components/shared/Modal';
import { getLeadEmails } from '../leads/leadEmailService';

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 8px;
`;

const Hint = styled.p`
  margin: 0;
  font-size: 0.78rem;
  line-height: 1.4;
  color: ${(p) => p.theme.colors?.textSecondary || '#6b7280'};
`;

const Option = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  padding: 9px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.88rem;
  color: inherit;
  background: ${(p) => (p.$active ? 'rgba(246, 150, 32, 0.08)' : 'transparent')};
  border: 1px solid ${(p) => (p.$active ? '#F69620' : (p.theme.colors?.border || '#d1d5db'))};

  input[type='radio'] {
    accent-color: #F69620;
    cursor: pointer;
    flex-shrink: 0;
  }

  .opt-text {
    word-break: break-all;
  }

  &:hover {
    border-color: #F69620;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;

  button {
    padding: 8px 16px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.88rem;
    border: 1px solid transparent;
  }

  .send-btn {
    background: #F69620;
    color: #fff;
  }

  .cancel-btn {
    background: transparent;
    color: inherit;
    border-color: ${(p) => p.theme.colors?.border || '#d1d5db'};
  }
`;

const SendEstimateModal = ({
  isOpen,
  estimateId,
  lead,
  title = 'Send Estimate',
  onCancel,
  onConfirm,
}) => {
  const [rows, setRows] = useState(null); // null = not loaded yet
  const [picked, setPicked] = useState('');

  const loadEmails = useCallback(async () => {
    if (!lead?.id) { setRows([]); return; }
    try {
      const res = await getLeadEmails(lead.id);
      setRows(res.data || []);
    } catch (e) {
      // Never block a send because the address list could not be fetched —
      // fall through to the lead's primary address.
      console.error('Error fetching lead email addresses:', e);
      setRows([]);
    }
  }, [lead]);

  useEffect(() => {
    if (!isOpen) return;
    setPicked('');
    setRows(null);
    loadEmails();
  }, [isOpen, loadEmails]);

  // Primary first, de-duped, primary always present even with no rows.
  const options = useMemo(() => {
    const seen = new Set();
    const out = [];
    const push = (addr, label, isPrimary) => {
      const value = String(addr || '').trim();
      if (!value.includes('@')) return;
      const key = value.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ value, label, isPrimary });
    };
    push(lead?.email, 'Primary', true);
    (rows || []).forEach((r) => push(r.email_address, r.label, r.is_primary));
    return out;
  }, [lead, rows]);

  if (!isOpen) return null;

  // Still loading, or nothing to choose between: the original confirm dialog.
  // `null` recipient means "let the server use the lead's primary address".
  if (rows === null || options.length <= 1) {
    return (
      <Modal
        isOpen={isOpen}
        title={title}
        message={`Send estimate #${estimateId} to ${lead?.name} (${lead?.email})? This will send an email with a link to view and sign the quote online${lead?.accept_sms ? ', and an SMS notification' : ''}.`}
        onConfirm={() => onConfirm(null)}
        onCancel={onCancel}
      />
    );
  }

  const selected = picked || options[0].value;

  return (
    <Modal isOpen={isOpen} title={title} hideFooter onCancel={onCancel}>
      <Body>
        <Hint>
          {`This lead has ${options.length} email addresses. Choose who to send estimate #${estimateId} to — any of them can open and sign the quote.`}
        </Hint>
        {options.map((opt) => {
          const active = selected.toLowerCase() === opt.value.toLowerCase();
          return (
            <Option
              key={opt.value}
              type="button"
              $active={active}
              onClick={() => setPicked(opt.value)}
            >
              <input type="radio" name="sendEstimateRecipient" checked={active} readOnly />
              <span className="opt-text">
                {opt.value}
                {opt.isPrimary ? ' (Primary)' : opt.label ? ` (${opt.label})` : ''}
              </span>
            </Option>
          );
        })}
        {lead?.accept_sms && lead?.phone && (
          <Hint>{`An SMS notification will also be sent to ${lead.phone}.`}</Hint>
        )}
        <Actions>
          <button className="send-btn" onClick={() => onConfirm(selected)}>Send</button>
          <button className="cancel-btn" onClick={onCancel}>Cancel</button>
        </Actions>
      </Body>
    </Modal>
  );
};

export default SendEstimateModal;
